import React from 'react';
import { Briefcase, Mail, User, Users } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import ContactPointsDisplay from './ContactPointsDisplay';
import type { ContactPoint } from './contactPoints';
import { groupContactPointsToOrganizationContacts } from './organizationContacts';
import { mutedTextClass } from './themeHelpers';

interface OrganizationContactsDisplayProps {
  points?: ContactPoint[];
}

const OrganizationContactsDisplay: React.FC<OrganizationContactsDisplayProps> = ({ points }) => {
  const { colors } = useTheme();
  const contacts = groupContactPointsToOrganizationContacts(points).filter(
    (contact) =>
      contact.contactName.trim() ||
      contact.contactPosition.trim() ||
      contact.emails.some((row) => row.value.trim()),
  );

  if (contacts.length === 0) {
    return <span className={mutedTextClass(colors)}>-</span>;
  }

  return (
    <div className={cn('overflow-hidden rounded-xl border', colors.border, 'bg-white dark:bg-gray-950/20')}>
      <div
        className={cn(
          'flex items-center gap-3 border-b px-3 py-2.5',
          colors.border,
          'bg-indigo-50/70 dark:bg-indigo-950/20',
        )}
      >
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-indigo-600 text-white">
          <Users className="h-4 w-4" aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <p className={cn('text-sm font-semibold', colors.text)}>Contacts</p>
          <p className={cn('text-xs', colors.textMuted)}>
            {contacts.length} {contacts.length === 1 ? 'person' : 'people'}
          </p>
        </div>
      </div>

      <div className="space-y-0 p-3">
        {contacts.map((contact, index) => {
          const isLast = index === contacts.length - 1;
          const emailPoints = contact.emails
            .filter((row) => row.value.trim())
            .map((row) => ({
              id: row.id,
              kind: 'EMAIL' as const,
              value: row.value,
              label: row.label,
              isPrimary: row.isPrimary,
            }));

          return (
            <div key={contact.key} className="flex gap-2.5">
              <div className="flex w-8 shrink-0 flex-col items-center">
                <div
                  className={cn(
                    'flex h-8 w-8 items-center justify-center rounded-full border text-xs font-semibold',
                    'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/60 dark:bg-emerald-950/40 dark:text-emerald-300',
                  )}
                >
                  {contact.contactName.trim() ? (
                    contact.contactName.trim().charAt(0).toUpperCase()
                  ) : (
                    <User className="h-3.5 w-3.5" aria-hidden />
                  )}
                </div>
                {!isLast ? (
                  <div className={cn('mt-1.5 w-px flex-1 min-h-4', 'bg-gray-200 dark:bg-gray-700')} aria-hidden />
                ) : null}
              </div>

              <div className={cn('mb-2 min-w-0 flex-1 rounded-lg border p-2.5', colors.border, 'bg-gray-50/50 dark:bg-gray-900/20')}>
                <p className={cn('text-[10px] font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-400')}>
                  Contact
                </p>
                <p className={cn('text-sm font-medium truncate', colors.text)}>
                  {contact.contactName.trim() || `Person ${index + 1}`}
                </p>
                {contact.contactPosition.trim() ? (
                  <p className={cn('mt-0.5 flex items-center gap-1 text-xs truncate', colors.textMuted)}>
                    <Briefcase className="h-3 w-3 shrink-0 opacity-70" aria-hidden />
                    {contact.contactPosition.trim()}
                  </p>
                ) : null}

                {emailPoints.length > 0 ? (
                  <div className="mt-2 space-y-1 border-t pt-2">
                    <p className={cn('flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider', colors.textMuted)}>
                      <Mail className="h-3 w-3" aria-hidden />
                      Emails
                    </p>
                    <ContactPointsDisplay points={emailPoints} kind="EMAIL" />
                  </div>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default OrganizationContactsDisplay;
